import type { Backtest, BacktestScope } from "../../../types/backtests";
import type { DateRange } from "../../../types/trade";
import type { WorkspaceNodeMeta } from "../../../types/workspace";
import { formatWorkspaceMetaWindowLabel } from "./workspaceEntityCardDetails";

function readTradeSymbol(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function readSymbolFromRecord(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  return readTradeSymbol((value as Record<string, unknown>).symbol);
}

/**
 * Trade symbol for rerun OHLC gating — scope may omit symbol on older saved sims.
 */
export function resolveBacktestTradeSymbol(input: {
  backtest?: Pick<Backtest, "scope" | "result" | "payload"> | null;
  meta?: WorkspaceNodeMeta | null;
}): string {
  const fromScope = readTradeSymbol(input.backtest?.scope?.symbol);
  if (fromScope) return fromScope;

  const fromResultMeta = readTradeSymbol(input.backtest?.result?.meta?.symbol);
  if (fromResultMeta) return fromResultMeta;

  const payload = input.backtest?.payload;
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const direct = readTradeSymbol(record.symbol);
    if (direct) return direct;

    for (const key of ["request", "simulate_request", "input"] as const) {
      const nested = readSymbolFromRecord(record[key]);
      if (nested) return nested;
    }

    const job = record.job;
    if (job && typeof job === "object") {
      const fromJob = readSymbolFromRecord(job);
      if (fromJob) return fromJob;
      const request = (job as Record<string, unknown>).request;
      const fromJobRequest = readSymbolFromRecord(request);
      if (fromJobRequest) return fromJobRequest;
    }
  }

  return "";
}

function parseCalendarYmd(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return new Date(y, mo - 1, d, 0, 0, 0, 0);
}

function ymdPairLabel(start: string, end: string): string {
  return start === end ? start : `${start} – ${end}`;
}

/**
 * Concrete current-window label for the Rerun dialog.
 * Prefer scope.window_label → scope local YMD → node meta (same as report chips).
 * Does not humanize timeframe presets.
 */
export function resolveRerunCurrentWindowLabel(input: {
  scope?: BacktestScope | null;
  meta?: WorkspaceNodeMeta | null;
}): string | null {
  const fromScopeLabel = input.scope?.window_label?.trim();
  if (fromScopeLabel) return fromScopeLabel;

  const scopeStart = input.scope?.local_start_date?.trim() || "";
  const scopeEnd = input.scope?.local_end_date?.trim() || "";
  if (scopeStart && scopeEnd) return ymdPairLabel(scopeStart, scopeEnd);

  return formatWorkspaceMetaWindowLabel(input.meta);
}

/**
 * Prefill date range from concrete locals on scope, else node meta.
 * Never treats `timeframe === "all-time"` as a wipe when dates exist.
 */
export function resolveRerunPrefillDateRange(input: {
  scope?: BacktestScope | null;
  meta?: WorkspaceNodeMeta | null;
}): DateRange | null {
  const scopeStart = input.scope?.local_start_date?.trim() || "";
  const scopeEnd = input.scope?.local_end_date?.trim() || "";
  if (scopeStart && scopeEnd) {
    const startDate = parseCalendarYmd(scopeStart);
    const endDate = parseCalendarYmd(scopeEnd);
    if (startDate && endDate) return { startDate, endDate };
  }

  const metaStart = input.meta?.localStartDate?.trim() || "";
  const metaEnd = input.meta?.localEndDate?.trim() || "";
  if (metaStart && metaEnd) {
    const startDate = parseCalendarYmd(metaStart);
    const endDate = parseCalendarYmd(metaEnd);
    if (startDate && endDate) return { startDate, endDate };
  }

  return null;
}
